import React, {useState} from 'react';
import PropTypes from 'prop-types';
import {useDispatch, useSelector} from 'react-redux';
import classNames from 'classnames';

import OutputText from '@ey/canvascoreservices/OutputText';

import {
	userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributes,
	newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributes,
	contactHasBeenDeletedErrorCodeCustomAttributes,
	newContactHasBeenDeletedErrorCodeCustomAttributes,
	projectSessionKeys
} from '../../../../../../util/uiconstants';
import {labels, currentResource} from '../../../../../../util/utils';
import {getFlattenedContactsFromEntities, getUniqueContacts} from '../../../../../../util/contactsDB';

import userListErrorMessage from './utils/userListErrorMessage';
import {CUSTOM_COLUMN, CUSTOM_COLUMN_ATTRIBUTE} from './utils/constants';
import updateCustomColumnsData from './utils/updateCustomColumnsData';
import getUnadministrableUsers from './utils/getUnadministrableUsers';

import {updateContactCustomColumns} from '../../../../../../actions/Project/projectContactAction';
import {updateDisplayData} from '../../../../../../actions/Metrics/dashboardContextAction';
import {raiseJSValidationError} from '../../../../../../actions/erroractions';

import useForm from '../../../../../CoreWrappers/Form/useForm';
import CustomAttributesPanel from '../CustomAttributesPanel';
import Button from '../../../../../CoreWrappers/Button';
import HrAlt from '../../../../../Common/Hr/HrAlt';
import StyledDCAForm from './StyledDCAForm';

const DCAForm = () => {
	const [isLoading, setIsLoading] = useState(false);

	const {customColumns, projectId} = useSelector(({project}) => ({
		customColumns: project?.customColumns ?? [],
		projectId: project?.projectId ?? ''
	}));
	const projectSessionKey = `${projectSessionKeys.userColumns}_${projectId}`;
	const userGui = useSelector(({userPreferences}) => userPreferences?.gui);
	const isProjectOwner = useSelector(
		({project}) => !!project?.users?.find(({userId}) => userId === userGui)?.isProjectOwner
	);
	const {selectedContacts, displayedEntities, entities} = useSelector(({contacts}) => ({
		selectedContacts: contacts?.selected,
		displayedEntities: contacts?.displayedEntities,
		entities: contacts?.entities
	}));
	const dashboardContext = useSelector(({dashboardContext}) => dashboardContext);

	const dispatch = useDispatch();

	const getEntityId = () => {
		let entityId = entities[0].entityId;
		if (!isProjectOwner) {
			entities.map((entity) => {
				if (entity.entityAdmins.some((e) => e.userId === userGui)) {
					entityId = entity.entityId;
				}
			});
		}
		return entityId;
	};

	const getCustomColumn = (values) => {
		const customColumnToUpdate = [{id: values[CUSTOM_COLUMN], optionId: values[CUSTOM_COLUMN_ATTRIBUTE]}];

		return customColumnToUpdate;
	};

	const generatePayload = (values) => ({
		data: {
			contactIds: selectedContacts,
			customColumns: getCustomColumn(values)
		},
		entityId: getEntityId()
	});

	const handleAllContactsNotEditable = (users) => {
		const userList = getUniqueContacts(getFlattenedContactsFromEntities(entities))
			.filter(({userId}) => users.includes(userId))
			.map(({name, lastName}) => `${name} ${lastName}`)
			.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
			.join(', ');
		const errorCode = userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributes;
		dispatch(
			raiseJSValidationError(
				`<strong>${errorCode}:</strong> ${currentResource.Errors[errorCode].replace(
					'{0}',
					`<strong>${userList}</strong>`
				)}`
			)
		);
	};

	const displayCustomError = (error) => {
		const errorCode = error?.response?.data?.errors?.Error?.[0]?.split('-')?.[0].trim() || '';
		if (errorCode === userIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributes) {
			const errorContacts = error?.response?.data?.errors?.Error[0]?.split(/[\-\:]/)[2] || '';
			const users = userListErrorMessage(entities, displayedEntities, userGui);
			const resouceLabel = currentResource.Errors[errorCode];
			let userList = [];
			users?.forEach((user) => userList.push(`${user.name} ${user.lastName}`));
			userList = userList.sort();
			let names = '';
			userList?.forEach((user, i) => {
				if (i === userList.length - 1) {
					names = names + user;
				} else {
					names = names + user + ', ';
				}
			});
			dispatch(
				raiseJSValidationError(
					`<strong>${newUserIsNotEntityAdminOfAllSelectedEntitiesErrorCodeCustomAttributes}:</strong> ${resouceLabel.replace(
						'{0}',
						errorContacts
					)}`
				)
			);
		}
		if (errorCode === contactHasBeenDeletedErrorCodeCustomAttributes) {
			const errorContacts = error?.response?.data?.errors?.Error[0]?.split(/[\-\:]/)[2] || '';
			const resouceLabel = currentResource.Errors[errorCode];
			dispatch(
				raiseJSValidationError(
					`<strong>${newContactHasBeenDeletedErrorCodeCustomAttributes}:</strong> ${resouceLabel.replace(
						'{0}',
						errorContacts
					)}`
				)
			);
		}
	};

	const initialValues = {
		[CUSTOM_COLUMN]: null,
		[CUSTOM_COLUMN_ATTRIBUTE]: null
	};

	const schema = {
		[CUSTOM_COLUMN]: {required: true},
		[CUSTOM_COLUMN_ATTRIBUTE]: {required: false}
	};

	const {values, errors, handleChange, handleSubmit, handleReset, isFormValid} = useForm({
		initialValues,
		schema,
		onSubmit: (values, callback) => {
			setIsLoading(true);

			let valid = true;

			const unadministrableUsers = getUnadministrableUsers(selectedContacts, userGui, entities);
			if (valid && !isProjectOwner && unadministrableUsers.length > 0) {
				handleAllContactsNotEditable(unadministrableUsers);
				setIsLoading(false);
				valid = false;
			}

			if (valid) {
				let payload = generatePayload(values);

				const newEntities = updateCustomColumnsData(entities, payload.data, customColumns);

				dispatch(updateContactCustomColumns(projectId, payload))
					.then(() => {
						dispatch(
							updateDisplayData(dashboardContext.engagements, newEntities, dashboardContext.orgChart, true)
						).then(() => callback());
					})
					.catch((error) => {
						displayCustomError(error);
						callback();
					});
			}
		}
	});

	const renderTitle = () => <OutputText>{labels.defineAttributes}</OutputText>;

	const renderControls = () => {
		const saveProps = {
			label: labels.save,
			onClick: () =>
				handleSubmit(() => {
					setIsLoading(false);
					handleReset();
				})
		};

		const cancelProps = {
			variant: 'ghost',
			label: labels.cancelLabel,
			onClick: () => false
		};

		return (
			<>
				<HrAlt className="controls-hr" />
				<div className="controls">
					<Button {...cancelProps} />
					<Button {...saveProps} />
				</div>
			</>
		);
	};

	const formProps = {values, errors, handleChange, handleSubmit, handleReset, isFormValid};
	const props = {...formProps, customColumns};
	const isEmpty = !customColumns?.length > 0;

	return (
		<StyledDCAForm isLoading={isLoading} className={classNames('customScrollbar', {empty: isEmpty})}>
			{renderTitle()}
			<CustomAttributesPanel {...props} />
			{!isEmpty && renderControls()}
		</StyledDCAForm>
	);
};

DCAForm.propTypes = {
	open: PropTypes.bool.isRequired
};

DCAForm.defaultProps = {};

export default DCAForm;
